import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Search as SearchIcon, X, Hash, Lock, Heart, MessageCircle } from 'lucide-react';

const Avatar = ({ src, name, size = 40 }) =>
  src ? (
    <img src={src} alt={name || ''} className="rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <div
      className="rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );

const UserRow = ({ u, t, onOpenProfile, testid }) => (
  <button
    type="button"
    onClick={() => onOpenProfile && onOpenProfile(u.id)}
    data-testid={testid}
    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-start"
  >
    <Avatar src={u.profile_picture_url} name={u.name} size={44} />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-900 truncate flex items-center gap-1">
        {u.name}
        {u.profile_privacy === 'private' && (
          <Lock className="w-3.5 h-3.5 text-gray-400 inline" aria-label={t('privateAccount')} />
        )}
      </p>
      {u.nickname && <p className="text-xs text-gray-500 truncate">@{u.nickname}</p>}
    </div>
    <span className="text-[11px] text-gray-400 shrink-0">
      {(u.followers_count ?? 0)} {t('followers')}
    </span>
  </button>
);

const PostThumb = ({ p, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="aspect-square bg-gray-100 overflow-hidden relative group"
  >
    {p.media_type === 'video' ? (
      <video src={p.media_url} className="w-full h-full object-cover" />
    ) : (
      <img src={p.media_url} alt="" className="w-full h-full object-cover" />
    )}
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-3 text-white opacity-0 group-hover:opacity-100">
      <span className="flex items-center gap-1 text-sm font-semibold">
        <Heart className="w-4 h-4" /> {p.likes_count || 0}
      </span>
      <span className="flex items-center gap-1 text-sm font-semibold">
        <MessageCircle className="w-4 h-4" /> {p.comments_count || 0}
      </span>
    </div>
  </button>
);

const HashtagRow = ({ tag, onClick, testid, t }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-start"
  >
    <div className="w-11 h-11 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
      <Hash className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-900 truncate">#{tag.tag}</p>
      <p className="text-xs text-gray-500">{t('hashtagPostsCount', { n: tag.posts_count })}</p>
    </div>
  </button>
);

const TABS = ['all', 'users', 'posts', 'tags'];

const Explore = ({ token, API, t, isRTL, currentUser, onOpenProfile, onOpenHashtag, onOpenPost }) => {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  const [results, setResults] = useState({ users: [], posts: [], hashtags: [] });
  const [trending, setTrending] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadDefaults = useCallback(async () => {
    setLoadingDefaults(true);
    try {
      const [tr, sg, pp] = await Promise.all([
        axios.get(`${API}/hashtags/trending?hours=72&limit=10`, { headers }),
        axios.get(`${API}/discover/users?limit=10`, { headers }),
        axios.get(`${API}/discover/posts?limit=18`, { headers }),
      ]);
      setTrending(tr.data || []);
      setSuggested(sg.data || []);
      setPopular(pp.data || []);
    } catch {
      setTrending([]); setSuggested([]); setPopular([]);
    } finally {
      setLoadingDefaults(false);
    }
    // eslint-disable-line
  }, [API, token]);

  useEffect(() => { loadDefaults(); }, [loadDefaults]);

  const runSearch = useCallback(
    async (query, filter) => {
      if (!query || query.trim().length < 2) {
        setResults({ users: [], posts: [], hashtags: [] });
        return;
      }
      setSearching(true);
      try {
        const res = await axios.get(`${API}/search`, {
          params: { q: query.trim(), type: filter, limit: 20 },
          headers,
        });
        setResults({
          users: res.data?.users || [],
          posts: res.data?.posts || [],
          hashtags: res.data?.hashtags || [],
        });
      } catch {
        setResults({ users: [], posts: [], hashtags: [] });
      } finally {
        setSearching(false);
      }
    },
    [API, token]
  );

  // Debounced search whenever q or tab changes
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(q, tab);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, tab, runSearch]);

  const showResults = q.trim().length >= 2;
  const totalResults =
    (results.users?.length || 0) + (results.posts?.length || 0) + (results.hashtags?.length || 0);

  return (
    <div data-testid="explore-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-3 py-3">
          <div className="relative">
            <SearchIcon
              className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'} w-4 h-4 text-gray-400`}
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
              data-testid="explore-search-input"
              className={`w-full ${isRTL ? 'pr-9 pl-9' : 'pl-9 pr-9'} py-2.5 rounded-xl bg-gray-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 text-sm`}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                data-testid="explore-search-clear"
                className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-1' : 'right-1'} p-2 min-w-[32px] min-h-[32px] inline-flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500`}
                aria-label={t('close')}
              >
                <X aria-hidden="true" className="w-4 h-4" />
              </button>
            )}
          </div>
          {!q && (
            <p
              data-testid="explore-search-hint"
              className="mt-1.5 px-1 text-[11px] text-gray-400"
            >
              {t('exploreSearchHint')}
            </p>
          )}

          {showResults && (
            <div className="mt-3 flex bg-gray-100 rounded-lg p-1 text-sm font-semibold">
              {TABS.map((k) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`explore-tab-${k}`}
                  className={`flex-1 py-1.5 rounded-md transition-colors ${
                    tab === k ? 'bg-white text-purple-700 shadow' : 'text-gray-600'
                  }`}
                >
                  {t(`searchTab${k.charAt(0).toUpperCase() + k.slice(1)}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 py-3 pb-24">
        {showResults ? (
          <section data-testid="explore-results" className="space-y-4">
            {searching && (
              <p
                role="status"
                aria-live="polite"
                className="text-center text-xs text-gray-400 py-2"
              >
                {t('loading')}
              </p>
            )}
            {!searching && totalResults === 0 && (
              <p data-testid="explore-no-results" className="text-center text-sm text-gray-500 py-10">
                {t('noResultsFor', { q })}
              </p>
            )}
            {(tab === 'all' || tab === 'tags') && results.hashtags.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
                {results.hashtags.map((h) => (
                  <HashtagRow
                    key={h.tag}
                    tag={h}
                    t={t}
                    testid={`hashtag-result-${h.tag}`}
                    onClick={() => onOpenHashtag(h.tag)}
                  />
                ))}
              </div>
            )}
            {(tab === 'all' || tab === 'users') && results.users.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
                {results.users.map((u) => (
                  <UserRow
                    key={u.id}
                    u={u}
                    t={t}
                    testid={`user-result-${u.id}`}
                    onOpenProfile={onOpenProfile}
                  />
                ))}
              </div>
            )}
            {(tab === 'all' || tab === 'posts') && results.posts.length > 0 && (
              <div data-testid="explore-posts-grid" className="grid grid-cols-3 gap-1">
                {results.posts.map((p) => (
                  <PostThumb
                    key={p.id}
                    p={p}
                    testid={`post-result-${p.id}`}
                    onClick={() => onOpenPost && onOpenPost(p)}
                  />
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="space-y-5">
            {/* Trending hashtags */}
            <div>
              <h2 className="px-1 mb-2 text-sm font-bold text-gray-900">{t('trendingHashtags')}</h2>
              <div
                data-testid="explore-trending-row"
                className="flex gap-2 overflow-x-auto no-scrollbar pb-1"
              >
                {loadingDefaults && trending.length === 0 && (
                  <div
                    data-testid="explore-trending-skeleton"
                    role="status"
                    aria-live="polite"
                    aria-label={t('loadingContent')}
                    className="flex gap-2"
                  >
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="shrink-0 w-32 rounded-xl border border-gray-200 bg-white overflow-hidden"
                      >
                        <div className="circleo-skeleton w-full h-20 rounded-none" />
                        <div className="p-2 space-y-2">
                          <div className="circleo-skeleton h-3 w-2/3" />
                          <div className="circleo-skeleton h-2.5 w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {trending.map((h) => (
                  <button
                    key={h.tag}
                    type="button"
                    onClick={() => onOpenHashtag(h.tag)}
                    data-testid={`trending-${h.tag}`}
                    className="shrink-0 w-32 rounded-xl border border-gray-200 bg-white overflow-hidden text-start"
                  >
                    {h.sample_media_url ? (
                      <img src={h.sample_media_url} alt="" className="w-full h-20 object-cover" />
                    ) : (
                      <div className="w-full h-20 bg-gradient-to-tr from-purple-100 to-pink-100 flex items-center justify-center text-purple-500">
                        <Hash className="w-7 h-7" />
                      </div>
                    )}
                    <div className="p-2">
                      <p className="text-sm font-semibold truncate">#{h.tag}</p>
                      <p className="text-[11px] text-gray-500">
                        {t('hashtagPostsCount', { n: h.posts_count })}
                      </p>
                    </div>
                  </button>
                ))}
                {!loadingDefaults && trending.length === 0 && (
                  <p className="text-xs text-gray-400 px-2 py-3">—</p>
                )}
              </div>
            </div>

            {/* Suggested users */}
            {suggested.length > 0 && (
              <div>
                <h2 className="px-1 mb-2 text-sm font-bold text-gray-900">{t('suggestedUsers')}</h2>
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
                  {suggested.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      t={t}
                      testid={`suggested-user-${u.id}`}
                      onOpenProfile={onOpenProfile}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Popular posts */}
            {popular.length > 0 && (
              <div>
                <h2 className="px-1 mb-2 text-sm font-bold text-gray-900">{t('popularPosts')}</h2>
                <div data-testid="explore-popular-grid" className="grid grid-cols-3 gap-1">
                  {popular.map((p) => (
                    <PostThumb
                      key={p.id}
                      p={p}
                      testid={`popular-post-${p.id}`}
                      onClick={() => onOpenPost && onOpenPost(p)}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default Explore;
